import { ICartItem } from '../_store/types'
import { IAutoAddingProduct } from '../_types'

const PATCHABLE_FIELDS = [
  'min_qty',
  'max_qty',
  'is_optional',
  'price',
  'price_with_discount',
  'quantity',
] as const satisfies ReadonlyArray<keyof ICartItem>

export type AutoAddMetadataPatch = Pick<
  ICartItem,
  (typeof PATCHABLE_FIELDS)[number]
>

function compareAutoAddMaxQty(
  cartValue: number | undefined,
  apiValue: number | undefined
): boolean {
  const resolvedCart = cartValue ?? Infinity
  const resolvedApi = apiValue ?? Infinity
  return resolvedCart === resolvedApi
}

export function buildAutoAddItemsPayload(
  cart: ICartItem[] | undefined
): Record<string, number> {
  return (cart ?? []).reduce<Record<string, number>>((acc, item) => {
    if (!item.product_id || item.default_product) return acc
    acc[item.product_id] = item.quantity
    return acc
  }, {})
}

export function getAutoAddMetadataPatch(
  cartItem: ICartItem,
  autoAddProduct: IAutoAddingProduct,
  options: { checkPrice?: boolean } = {}
): AutoAddMetadataPatch | null {
  const patch: Partial<AutoAddMetadataPatch> = {}
  const minQty = autoAddProduct.min_qty ?? 1
  const maxQty = autoAddProduct.max_qty ?? Infinity
  const isOptional = !!autoAddProduct.is_optional
  const price = autoAddProduct.product?.out_price ?? 0
  const clampedQuantity = Math.min(Math.max(cartItem.quantity, minQty), maxQty)

  if (cartItem.quantity !== clampedQuantity) patch.quantity = clampedQuantity

  if (cartItem.min_qty !== minQty) patch.min_qty = minQty

  if (!compareAutoAddMaxQty(cartItem.max_qty, autoAddProduct.max_qty)) {
    if (autoAddProduct.max_qty !== undefined) {
      patch.max_qty = autoAddProduct.max_qty
    } else if (cartItem.max_qty !== undefined) {
      patch.max_qty = undefined
    }
  }

  if (!!cartItem.is_optional !== isOptional) patch.is_optional = isOptional

  if (options.checkPrice) {
    const cartPrice = cartItem.price ?? 0
    if (cartPrice !== price) {
      patch.price = price
      patch.price_with_discount = price
    }
  }

  return Object.keys(patch).length > 0 ? (patch as AutoAddMetadataPatch) : null
}

export type AutoAddCartSyncAction =
  | { type: 'add'; product: IAutoAddingProduct; isDefault: boolean }
  | { type: 'patch'; key: string; patch: AutoAddMetadataPatch }
  | { type: 'remove'; key: string }

export function planAutoAddCartSync(
  cart: ICartItem[],
  defaultProducts: IAutoAddingProduct[] | undefined,
  needRecommendProducts: IAutoAddingProduct[] | undefined,
  options: { checkPrice?: boolean } = {}
): AutoAddCartSyncAction[] {
  const actions: AutoAddCartSyncAction[] = []
  const defaults = defaultProducts ?? []
  const recommends = needRecommendProducts ?? []

  defaults.forEach((element) => {
    const current = cart.find((item) => item.product_id === element.product_id)
    if (!current) {
      actions.push({ type: 'add', product: element, isDefault: true })
      return
    }

    const patch = getAutoAddMetadataPatch(current, element, options)
    if (patch) {
      actions.push({ type: 'patch', key: current.key, patch })
    }
  })

  recommends.forEach((element) => {
    const current = cart.find((item) => item.product_id === element.product_id)
    if (!current) return

    const patch = getAutoAddMetadataPatch(current, element, options)
    if (patch) {
      actions.push({ type: 'patch', key: current.key, patch })
    }
  })

  cart.forEach((item) => {
    if (!item.default_product) return

    const stillValid =
      defaults.some((product) => product.product_id === item.product_id) ||
      recommends.some((product) => product.product_id === item.product_id)

    if (!stillValid) {
      actions.push({ type: 'remove', key: item.key })
    }
  })

  return actions
}

export function hasAutoAddCartSyncActions(
  actions: AutoAddCartSyncAction[]
): boolean {
  return actions.length > 0
}
